import { useEffect, useState } from 'react'
import { getApiDataProgressPayment, liveUrl } from '../APIHelper'
import PaymentStatus, { normalizeApiStatus } from '../PaymentStatus'
import CheckoutSkeleton from '../CheckoutSkeleton'
import { BrandHeader, PoweredBy, applyBackendBranding, getBranding } from '../Branding'
import { checkNull, redirectToUrl } from '../../Helpers'
import { Strings } from '../../Strings'

/**
 * PaymentStatusPage — standalone page for /payment-status/:transaction_code/:order_code
 * and ?order_code= subscription status routes.
 *
 * Fetches the payment/subscription status from the API and renders the PaymentStatus component.
 */
const PaymentStatusPage = ({ orderCode, isSubscription, onPaymentDone, redirectUrl, ...brandingProps }) => {
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState(Strings.fail)
  const [message, setMessage] = useState('')
  // branding saved in the AautiPay portal, returned with the status
  const [backendBranding, setBackendBranding] = useState(null)

  // Notify the host app without letting a host-side error flip a fetched status to failure
  const notifyPaymentDone = (payload) => {
    try {
      onPaymentDone?.(payload)
    } catch (error) {
      console.error('onPaymentDone callback failed:', error)
    }
  }

  // Reaching this page means the gateway has finished, so leave for the merchant's
  // redirect_url whenever there is one — whatever the status turned out to be.
  const redirectIfUrlPresent = (apiRedirectUrl) => {
    redirectToUrl(redirectUrl || apiRedirectUrl)
  }

  useEffect(() => {
    if (isSubscription) {
      fetchSubscriptionStatus(orderCode)
    } else {
      fetchPaymentStatus(orderCode)
    }
  }, [orderCode])

  async function fetchPaymentStatus(code) {
    setLoading(true)
    try {
      const response = await getApiDataProgressPayment(
        `${liveUrl}pay-response/pay-in/${code}`,
        'GET'
      )
      if (response?.status === false || checkNull(response)) {
        setStatus(Strings.fail)
        setMessage(response?.message || Strings.went_wrong)
      } else {
        const res = response?.data || response
        setBackendBranding(res?.branding || null)
        const normalizedStatus = normalizeApiStatus(res?.status)
        setStatus(normalizedStatus)
        setMessage(res?.fail_reason || res?.message || '')
        notifyPaymentDone(normalizedStatus)
        redirectIfUrlPresent(res?.redirect_url)
      }
    } catch (error) {
      setStatus(Strings.fail)
      setMessage(error?.message || Strings.went_wrong)
    } finally {
      setLoading(false)
    }
  }

  async function fetchSubscriptionStatus(code) {
    setLoading(true)
    try {
      const response = await getApiDataProgressPayment(
        `${liveUrl}getCashfreeSubscriptionStatus/${code}/test`,
        'GET'
      )
      if (response?.status) {
        const subStatus = response?.data?.subscription_status
        const apiRedirectUrl = response?.data?.redirect_url
        setBackendBranding(response?.data?.branding || null)
        if (subStatus === 'ACTIVE') {
          setStatus(Strings.subscriptionSuccess)
          setMessage(Strings.subscriptionSuccess)
          notifyPaymentDone(Strings.subscriptionSuccess)
        } else if (subStatus === Strings.bank_approval_pending) {
          setStatus(Strings.subPending)
          setMessage('Bank approval in progress')
          notifyPaymentDone(Strings.bank_approval_pending)
        } else if (subStatus === Strings.initialized) {
          setStatus('Payment Cancelled')
          setMessage(Strings.fail)
        } else {
          setStatus(Strings.fail)
          setMessage('')
        }
        redirectIfUrlPresent(apiRedirectUrl)
      } else {
        setStatus(Strings.fail)
        setMessage(Strings.went_wrong)
      }
    } catch (error) {
      setStatus(Strings.fail)
      setMessage(error?.message || Strings.went_wrong)
    } finally {
      setLoading(false)
    }
  }

  const brand = getBranding(applyBackendBranding(brandingProps, backendBranding))
  const color = brand.primaryColor

  // the merchant's branding arrives with the status, so show a neutral skeleton until then
  if (loading) {
    return <CheckoutSkeleton variant='status' />
  }

  return (
    <div style={{ display: 'flex', height: '100%', width: '100%', flexDirection: 'column', backgroundColor: brand.themeColor, overflow: 'hidden' }}>
      {/* Header */}
      <BrandHeader text={brand.headerText} color={brand.headerColor} textColor={brand.headerTextColor} />
      {/* Status — scrollable middle */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflowY: 'auto', overflowX: 'hidden', minHeight: 0 }}>
        <PaymentStatus
          primaryColor={color}
          loaderColor={brand.loaderColor}
          status={status}
          message={checkNull(message) ? 'Authentication Failed' : message}
        />
      </div>
      {/* Footer */}
      <PoweredBy color={color} />
    </div>
  )
}

export default PaymentStatusPage
